import * as angular from "angular";
import * as moment from "moment";
import {StaffTestImportService} from "../services/staff-test-import/staff-test-import.service";
import {StaffTestImportStatus, StaffMatchStatuses} from "../models/staff-test-import-status.model";
import {Column, SortDirections} from "../models/column.model";
import {StaffImportRecordsService} from "../services/staff-import-records/staff-import-records.service";
import {StaffImportPostOptions} from "../models/staff-import-status.model";
import {ConfigurationService} from "../services/configuration/configuration.service";
import {TitleToRoleConfiguration} from "../models/title-to-role-configuration.model";
import {StaffImportStatus} from "../models/staff-import-status.model";

const STAFF_TEST_IMPORT_STATUS = "staffTestImportStatus";
const STAFF_IMPORT_STATUS = "staffImportStatus";
const CONFIRM_STAFF_ROLES_TOOLTIP_TEXT = "Please confirm staff roles before import.";
const IS_NEW_EXPERIENCE = "isNewExperience";
const HAS_SAVED_ROLES = "hasSavedRoles";

export class StaffTestImportController {
    staffTestImportStatus: StaffTestImportStatus;
    isTestImportInProgress: boolean;
    isStaffTestImportStarted: boolean = false;
    columns: Column[];
    rows: StaffMatchStatuses[];
    isPromptVisible: boolean = false;
    checkboxChecked: boolean = false;
    isAllowedToStartImport: boolean = false;
    hasInactiveStaffWarning: boolean = false;
    districtRow;
    isRoleDrawerOpen: boolean = false;
    roleConfigurationsSaved: boolean = false;
    isNewExperience: boolean;
    confirmTooltip: boolean = false;
    staffImportStatus: StaffImportStatus;
    isTestImportSuccessAlertVisible: boolean = false;
    isRoleConfigSavedSuccessAlertVisible: boolean = false;
    testImportRedoTime: string;
    testImportRedoCompletionTime: string;
    hasSavedRoles: boolean;

    /* @ngInject */
    constructor(
        private $routeParams: { districtId: string },
        private $location: angular.ILocationService,
        private $interval: angular.IIntervalService,
        private $route: angular.route.IRouteService,
        private staffTestImportService: StaffTestImportService,
        private $timeout: angular.ITimeoutService,
        private staffImportRecordsService: StaffImportRecordsService,
        private staffInactiveMaxPercentage,
        private hasInactiveWarning,
        private $rootScope: angular.IRootScopeService,
        private configurationService: ConfigurationService
    ) {
        this.isNewExperience = this.$route.current.locals[IS_NEW_EXPERIENCE];
    }

    $onInit() {
        this.staffTestImportStatus = this.$route.current.locals[STAFF_TEST_IMPORT_STATUS];
        this.staffImportStatus = this.$route.current.locals[STAFF_IMPORT_STATUS];
        this.hasSavedRoles = this.$route.current.locals[HAS_SAVED_ROLES];
        this.$rootScope.$on("role-drawer:close", this.handleCloseDrawer);
        this.initColumns();
        this.initRows();

        this.initRoleConfigurations();

        this.testImportRedoTime = this.staffTestImportService.getLastStartedOnTimeForRedo();
        this.testImportRedoCompletionTime= this.staffTestImportService.getLastCompletionTimeForRedo();
    }

    /* istanbul ignore next */
    private renderSchoolNameHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            if (params.data.meta) {
                return `<div class="test-import__table-alert"> {{ data.schoolName }} </div>`;
            }
            return `{{ data.schoolName }}`;
        } else if (params.data.districtName) {
            if (params.data.meta) {
                return `<div class="test-import__table-alert"> {{ data.districtName }} </div>`;
            }
            return `{{ data.districtName }}`;
        }
    }

    private initColumns() {
        this.columns = [
            {
                headerName: "School",
                field: "schoolName",
                initialSort: ("ASC" as SortDirections),
                cellRenderer: this.renderSchoolNameHtml
            },
            {headerName: "Status", field: "status"},
            {headerName: "Inactive", field: "totalStaffDeactivated", cellRenderer: this.renderDeactivatedStaffHtml},
            {headerName: "Created", field: "totalStaffCreated", cellRenderer: this.renderCreatedStaffHtml},
            {headerName: "Matched", field: "totalStaffMatched", cellRenderer: this.renderMatchedStaffHtml}
        ];
    }

    /* istanbul ignore next */
    private renderMatchedStaffHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            return `
                {{ data.totalStaffMatched }}
                <download-csv
                    district="vm.$routeParams.districtId"
                    school="data.schoolId"
                    status="matched"
                    import-type="staff"
                ></download-csv>
            `;
        } else if (params.data.districtName) {
            return `
                {{ data.totalStaffMatched }}
                <download-csv
                    district="vm.$routeParams.districtId"
                    status="matched"
                    import-type="staff"
                ></download-csv>
            `;
        } else {
            return `${params.data.totalStaffMatched}`;
        }
    }

    /* istanbul ignore next */
    private renderCreatedStaffHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            return `
                {{ data.totalStaffCreated }}
                <download-csv
                    district="vm.$routeParams.districtId"
                    school="data.schoolId"
                    status="created"
                    import-type="staff"
                ></download-csv>
            `;
        } else if (params.data.districtName) {
            return `
                {{ data.totalStaffCreated }}
                <download-csv
                    district="vm.$routeParams.districtId"
                    status="created"
                    import-type="staff"
                ></download-csv>
            `;
        } else {
            return `${params.data.totalStaffCreated}`;
        }
    }

    /* istanbul ignore next */
    private renderDeactivatedStaffHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            return `
                {{ data.totalStaffDeactivated }}
                <download-csv
                    district="vm.$routeParams.districtId"
                    school="data.schoolId"
                    status="deactivated"
                    import-type="staff"
                ></download-csv>
            `;
        } else if (params.data.districtName) {
            return `
                {{ data.totalStaffDeactivated }}
                <download-csv
                    district="vm.$routeParams.districtId"
                    status="deactivated"
                    import-type="staff"
                ></download-csv>
            `;
        } else {
            return `${params.data.totalStaffDeactivated}`;
        }
    }

    private initRows(): StaffMatchStatuses[] {
        if (!this.staffTestImportStatus.staffMatchStatuses) {
            return;
        }

        this.staffTestImportStatus.staffMatchStatuses.forEach((rowStatus: any) => {
            if (rowStatus.districtName) {
                this.districtRow = rowStatus;
            }
            if (this.hasInactiveWarning && (
                rowStatus.totalStaffDeactivated &&
                rowStatus.totalStaffDeactivated >= (rowStatus.totalStaff * this.staffInactiveMaxPercentage)
            )) {
                rowStatus.meta = {rowStatus: "WARNING"};
                this.hasInactiveStaffWarning = true;
            }
            rowStatus.status = "Test Import Complete";
        });

        this.rows = this.staffTestImportStatus.staffMatchStatuses;
    }

    goBackToLanding() {
        return this.$location.path(`${this.$routeParams.districtId}/`);
    }

    startStaffTestImport(): void {
        if (this.hasSavedRoles) {
            this.staffTestImportService.staffTestImport(this.$route.current.params.districtId);
            this.isStaffTestImportStarted = true;
            if (!this.staffTestImportStatus.startedOn) {
                let interval = this.$interval(() => this.updateStaffTestImportStatusData(interval), 1000);
            }
        } else {
            this.$location.path(`${this.$routeParams.districtId}/staff-roles`).search("initial", true);
        }

    }

    private updateStaffTestImportStatusData(interval) {
        if (this.staffTestImportStatus.startedOn) {
            this.$interval.cancel(interval);
            this.initRows();
            return;
        }

        this.staffTestImportService.getStaffTestImportStatus(this.$route.current.params.districtId)
            .then(testImportStatus => {
                this.staffTestImportStatus = testImportStatus;
            });
    }

    isStaffTestImportInProgress(): boolean {

        if (this.testImportRedoTime && this.testImportRedoTime === this.staffTestImportStatus.startedOn) {
            return true;
        }
        return !this.isStaffTestImportCompleted() && !this.isStaffTestImportNotStarted();

    }

    isStaffTestImportCompleted(): boolean {
        if (!this.staffTestImportStatus) {
            return false;
        }
        if (this.testImportRedoTime && this.testImportRedoTime === this.staffTestImportStatus.startedOn) {
            return false;
        }

        const districtMatchStatus = this.staffTestImportStatus.districtMatchStatus;

        return this.checkStaffTestImportStartedOn() && !this.checkStaffTestImportCompletedOn() &&
            this.staffTestImportStatus.staffMatchStatuses &&
            this.staffTestImportStatus.staffMatchStatuses.length >= districtMatchStatus.totalSchools;
    }

    private checkStaffTestImportStartedOn(): boolean {
        return this.staffTestImportStatus.startedOn !== undefined && this.staffTestImportStatus.startedOn !== null;
    }

    private checkStaffTestImportCompletedOn(): boolean {
        return this.staffTestImportStatus.completedOn === undefined || this.staffTestImportStatus.completedOn === null;
    }

    isStaffTestImportNotStarted() {
        return !this.testImportRedoTime && !(this.staffTestImportStatus && this.staffTestImportStatus.startedOn);
    }

    getTestImportProgressStatus(): void {
            this.staffTestImportService.getStaffTestImportStatus(this.$route.current.params.districtId)
                .then(testImportStatus => {
                    this.staffTestImportStatus = testImportStatus;
                    this.initRows();
                    this.isTestImportSuccessAlertVisible = this.isStaffTestImportCompleted();
                    if(this.testImportRedoTime){
                        this.staffTestImportStatus.completedOn= this.testImportRedoCompletionTime
                    }

                });
         this.isTestImportInProgress = true;
        this.$timeout(() => {
            this.isTestImportInProgress = false;
        }, 5000);
    }

    getTestImportCompleteDate(): string {
        if (!this.staffTestImportStatus) {
            return;
        }

        let momentLocal = (moment as any).tz(this.staffTestImportStatus.completedOn, "UTC").local();
        if(this.testImportRedoTime){
            this.staffTestImportStatus.completedOn= this.testImportRedoCompletionTime;
             momentLocal = this.testImportRedoCompletionTime;
        }
        return `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
    }

    openPrompt() {
        this.isPromptVisible = true;
    }

    togglePrompt() {
        this.isPromptVisible = !this.isPromptVisible;
    }

    toggleAllowDisallowStaffImport(): void {
        this.isAllowedToStartImport = this.checkboxChecked;
    }

    startStaffImport() {
        if (this.isAllowedToStartImport) {
            const options: StaffImportPostOptions = {
                districtId: this.$routeParams.districtId,
                commandType: "IMPORT"
            };

            this.staffImportRecordsService.staffImport(options);
            return this.$location.path(`${this.$routeParams.districtId}/staff-import-records`);
        }
        this.confirmTooltip = true;
    }

    redoStaffTestImport() {
        const startTime = (moment as any).tz(new Date(), "UTC").local();
        // const startTime= `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
        this.staffTestImportStatus = {
            startedOn: startTime
        } as any;
        this.staffTestImportService.setLastStartedOnTimeForRedo(this.staffTestImportStatus.startedOn);
        this.testImportRedoTime = this.staffTestImportStatus.startedOn;

        this.staffTestImportService.redoStaffTestImport(this.$routeParams.districtId);
            this.isStaffTestImportStarted = true;
            this.hasInactiveStaffWarning = false;
            this.initRows();
            // setting redo completion time once redo happened.
            this.testImportRedoCompletionTime=(moment as any).tz(new Date(), "UTC").local();
            this.staffTestImportStatus.completedOn=this.testImportRedoCompletionTime;


    }

    isRedoTestImportRequired(): boolean {
        return !this.isRoleDrawerOpen && (
            this.isRedoTestImportRequiredBeforeInitialImport() || this.isRedoTestImportRequiredAfterInitialImport()
        );
    }

    isRedoTestImportRequiredBeforeInitialImport(): boolean {
        const testStatus = this.staffTestImportStatus;
        return testStatus.hoursBetweenLastTestImportAndNow >= testStatus.testImportHoursBeforeInitialImport &&
            !this.staffImportStatus;
    }

    isRedoTestImportRequiredAfterInitialImport(): boolean {
        return this.staffTestImportStatus.hoursBetweenLastTestImportAndNow >= this.staffTestImportStatus.testImportHoursBeforeReimport &&
            (this.staffImportStatus && this.staffImportStatus.completedOn != null);
    }

    initRoleConfigurations() {
        this.configurationService.getTitleToRoleConfiguration(this.$routeParams.districtId)
            .then((response: TitleToRoleConfiguration) => {
                if (!(response.defaultConfigurations === undefined ||
                    response.defaultConfigurations === null ||
                    response.defaultConfigurations.length === 0)) {
                    this.roleConfigurationsSaved = true;
                }
            });
    }

    private areRoleConfigurationsNotSaved(): boolean {
        return !(this.isRoleDrawerOpen || this.roleConfigurationsSaved);
    }

    showStaffImportButtonTooltip(): boolean {
        return (this.isRedoTestImportRequired() && (
                this.isRedoTestImportRequiredBeforeInitialImport() || this.isRedoTestImportRequiredAfterInitialImport())) ||
            this.areRoleConfigurationsNotSaved();
    }

    showRedoTooltipMessage(): string {
        if (this.isRedoTestImportRequired() && this.isRedoTestImportRequiredBeforeInitialImport()) {
            return `It has been over ${this.staffTestImportStatus.testImportHoursBeforeInitialImport} hours since the test import. 
                A test import should be re-run for an accurate data import.`;
        } else if (this.isRedoTestImportRequired() && this.isRedoTestImportRequiredAfterInitialImport()) {
            return `It has been over ${this.staffTestImportStatus.testImportHoursBeforeReimport} hours since the test import. 
                A test import should be re-run for an accurate data import.`;
        } else if (this.areRoleConfigurationsNotSaved()) {
            return CONFIRM_STAFF_ROLES_TOOLTIP_TEXT;
        }
    }

    shouldShowWarning(): boolean {
        return this.isStaffTestImportCompleted() && this.hasInactiveStaffWarning;
    }

    handleCloseDrawer = (_event, data) => {
        this.isRoleDrawerOpen = false;
        // TODO: Handle this with separate page
        if (data === "Saved") {
            this.initRoleConfigurations();
        }
    }

    toggleConfirmTooltip() {
        this.confirmTooltip = false;
    }

    getStaffRolesPath() {
        return `#/${this.$routeParams.districtId}/staff-roles`;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/staff-test-import/staff-test-import.controller.ts